import type { DateRange, DateRangePreset } from '@/components/ui/DateRangePicker';

/**
 * Presets temporels « rétrospectifs » partagés entre le Dashboard et le
 * Pipeline (mêmes libellés, mêmes bornes — single source of truth). Pas de
 * presets futurs ici (réservés à la card Trésorerie de KPI Direction).
 *
 * Bornes en UTC pour ne jamais dériver les buckets `date_trunc` côté Postgres
 *.
 */

export function utcStart(y: number, m: number, d: number): string {
  return new Date(Date.UTC(y, m, d)).toISOString();
}
export function utcEndOfDay(y: number, m: number, d: number): string {
  return new Date(Date.UTC(y, m, d, 23, 59, 59, 999)).toISOString();
}

export type PeriodPresetKey = 'week' | 'month' | 'quarter' | 'year' | 'last12months';

export const PERIOD_PRESETS: ReadonlyArray<DateRangePreset<PeriodPresetKey>> = [
  {
    key: 'week',
    label: 'Cette semaine',
    range: () => {
      const now = new Date();
      const day = now.getDay() || 7; // 1..7 (lundi=1)
      const monday = new Date(
        Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() - (day - 1)),
      );
      const sunday = new Date(monday.getTime() + 7 * 86_400_000 - 1);
      return { from: monday.toISOString(), to: sunday.toISOString() };
    },
  },
  {
    key: 'month',
    label: 'Ce mois',
    range: () => {
      const n = new Date();
      return {
        from: utcStart(n.getUTCFullYear(), n.getUTCMonth(), 1),
        to: utcEndOfDay(n.getUTCFullYear(), n.getUTCMonth() + 1, 0),
      };
    },
  },
  {
    key: 'quarter',
    label: 'Ce trimestre',
    range: () => {
      const n = new Date();
      const q = Math.floor(n.getUTCMonth() / 3);
      return {
        from: utcStart(n.getUTCFullYear(), q * 3, 1),
        to: utcEndOfDay(n.getUTCFullYear(), q * 3 + 3, 0),
      };
    },
  },
  {
    key: 'year',
    label: 'Cette année',
    // Décision produit 2026-07-02 (demande client) : « Cette année » = fenêtre
    // « année d'effet » décalée d'un jour — du 02 janvier N au 01 janvier N+1
    // (inclus). Partagée Dashboard + Pipeline et alignée avec DIRECTION_PRESETS
    // (KPI Direction) + la card Trésorerie (`presetPeriod('year')`). Remplace
    // l'année civile stricte 01/01→31/12 (ancienne décision 2026-06-28).
    range: () => {
      const y = new Date().getUTCFullYear();
      return {
        from: utcStart(y, 0, 2), // 02 janv. N 00:00 UTC
        to: utcEndOfDay(y + 1, 0, 1), // 01 janv. N+1 23:59:59.999 UTC (inclus)
      };
    },
  },
  {
    key: 'last12months',
    label: '12 derniers mois',
    range: () => {
      const n = new Date();
      const from = new Date(Date.UTC(n.getUTCFullYear() - 1, n.getUTCMonth() + 1, 1));
      return {
        from: from.toISOString(),
        to: utcEndOfDay(n.getUTCFullYear(), n.getUTCMonth(), n.getUTCDate()),
      };
    },
  },
];

/** Range par défaut partagée : « Cette année ». */
export function defaultPeriodRange(): DateRange {
  return PERIOD_PRESETS.find((p) => p.key === 'year')!.range();
}

/**
 * Retrouve la clé du preset dont la range courante correspond EXACTEMENT à
 * `range` (comparaison stricte from/to). Permet de réafficher le badge actif
 * après un reload/deep-link sans stocker la clé en URL. Retourne `null` si
 * aucune correspondance (plage custom).
 */
export function detectPeriodPreset(range: DateRange): PeriodPresetKey | null {
  for (const p of PERIOD_PRESETS) {
    const r = p.range();
    if (r.from === range.from && r.to === range.to) return p.key;
  }
  return null;
}

/**
 * Variante date-seule (`YYYY-MM-DD`) de `detectPeriodPreset`. Pour les
 * contextes qui stockent les bornes en date pure — typiquement le filtre
 * `effective_*` du Pipeline, validé `^\d{4}-\d{2}-\d{2}$` côté backend. On
 * compare les bornes du preset tronquées aux 10 premiers caractères (date).
 */
export function detectPeriodPresetDateOnly(
  fromDate: string,
  toDate: string,
): PeriodPresetKey | null {
  for (const p of PERIOD_PRESETS) {
    const r = p.range();
    if (r.from.slice(0, 10) === fromDate && r.to.slice(0, 10) === toDate) return p.key;
  }
  return null;
}
